import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';

const ToastContext = createContext(null);

/**
 * Replaces React Native's Alert.alert on the web.
 *
 * `toast(...)` is the non-blocking notice used for successes and errors, and
 * `confirm(...)` returns a promise that resolves true/false so destructive
 * actions read the same way they do in the app:
 *
 *   if (await confirm({ title: 'Cancel order', danger: true })) …
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const resolver = useRef(null);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, options = {}) => {
    const id = nextId.current;
    nextId.current += 1;
    const item = {
      id,
      title: options.title || '',
      message,
      tone: options.tone || 'info',
    };
    setToasts((list) => [...list, item]);
    setTimeout(() => dismiss(id), options.duration || 4200);
    return id;
  }, [dismiss]);

  const success = useCallback((message, title) => toast(message, { tone: 'success', title }), [toast]);
  const error = useCallback((message, title) => toast(message, { tone: 'error', title }), [toast]);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolver.current = resolve;
    setDialog({
      title: options.title || 'Are you sure?',
      message: options.message || '',
      confirmLabel: options.confirmLabel || 'Confirm',
      cancelLabel: options.cancelLabel || 'Cancel',
      danger: Boolean(options.danger),
    });
  }), []);

  const settle = useCallback((value) => {
    setDialog(null);
    if (resolver.current) {
      resolver.current(value);
      resolver.current = null;
    }
  }, []);

  const value = useMemo(
    () => ({ toast, success, error, confirm }),
    [toast, success, error, confirm],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`} onClick={() => dismiss(t.id)}>
            <span style={{ fontSize: 15 }}>
              {t.tone === 'success' ? '✅' : t.tone === 'error' ? '⚠️' : 'ℹ️'}
            </span>
            <div className="grow">
              {t.title ? <div className="toast-title">{t.title}</div> : null}
              <div>{t.message}</div>
            </div>
          </div>
        ))}
      </div>

      {dialog ? (
        <div className="modal-backdrop" onClick={() => settle(false)} role="presentation">
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="h3">{dialog.title}</h3>
            {dialog.message ? (
              <p className="t-body" style={{ marginTop: 8, lineHeight: 1.55 }}>{dialog.message}</p>
            ) : null}
            <div className="row" style={{ marginTop: 22, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-light" onClick={() => settle(false)}>
                {dialog.cancelLabel}
              </button>
              <button
                type="button"
                className={`btn ${dialog.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => settle(true)}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
export default ToastContext;
